import { z } from 'zod';

/**
 * ÉN NETTADRESSE-REGEL FOR HELE TVITVI (QA-runde 12, #16c → J4).
 *
 * ================================================================================================
 * FUNNET, ORDRETT: «En nettadresse uten https:// blokkerer oppretting. "godnokfestivalen.no" gir
 * "Formatet er ikke gyldig". Atle: det skal ikke ha noe å si.»
 *
 * Han har rett, og feilen er ikke i skjemaet — den er i at `z.url()` sto skrevet ni steder. Ingen
 * av dem var gale hver for seg; til sammen var de ni anledninger til å svare forskjellig på det
 * samme spørsmålet. Dette er stedet som svarer én gang.
 *
 * ================================================================================================
 * REGLENE, OG HVORFOR HVER AV DEM ER DER.
 *
 *  1. **Mangler skjemaet, er det `https://`.** Det er hele funnet. «godnokfestivalen.no»,
 *     «www.godnokfestivalen.no» og «GodNokFestivalen.no/program» går alle inn.
 *  2. **Et skjema som ER skrevet, blir stående.** Skriver noen `http://`, mente de `http://` —
 *     et lydanlegg eller en intern billettserver på festivalområdet har ofte ikke sertifikat, og
 *     en stille oppgradering til https ville gitt en lenke som ikke åpner. Vi gjetter bare der
 *     brukeren ikke har sagt noe.
 *  3. **Bare `http` og `https`.** `javascript:`, `data:` og `file:` er ikke nettadresser, de er
 *     angrepsflater: verdien her havner i en `href` som noen trykker på.
 *  4. **`www` verken legges til eller fjernes.** Begge godtas. Å stryke `www.` er en omskriving av
 *     noens adresse, og det finnes nettsteder der de to er forskjellige verter.
 *  5. **Verten må ha et punktum**, eller være `localhost`. Uten den regelen blir «Standing
 *     Ovation» til `https://standing%20ovation` og lagres som en gyldig adresse — en skrivefeil
 *     som ser ut som en lenke er verre enn en avvisning.
 *  6. **Verten settes i små bokstaver, resten ikke.** Et vertsnavn er ikke versalfølsomt; en sti
 *     er det.
 *  7. **Én etterfølgende skråstrek på en bar adresse fjernes**, slik at `festival.no` og
 *     `festival.no/` blir den samme raden i basen.
 *
 * HJELPETEKSTEN «Hele adressen, med https:// foran» ER OPPHEVET AV DETTE. Den beskrev en regel
 * som ikke lenger finnes, og #16c ber uttrykkelig om at den fjernes.
 */

/** `scheme://` i begynnelsen av en streng — den utvetydige formen. */
const SCHEME_WITH_SLASHES = /^[a-z][a-z0-9+.-]*:\/\//i;

/**
 * `noe:noeannet` uten skråstreker. To helt forskjellige ting ser slik ut, og forskjellen er den
 * ene fella i denne fila: `mailto:atle@godnok.no` er et skjema vi ikke vil ha, mens
 * `localhost:5173` og `festival.no:8080` er en vert med et portnummer.
 *
 * Målt, ikke gjettet: den første versjonen her brukte `^[a-z][a-z0-9+.-]*:` alene, og den leste
 * «localhost» som et skjema. Testen «lets localhost through» var rød på nøyaktig det.
 */
const COLON_PREFIX = /^([a-z][a-z0-9+.-]*):(.*)$/i;

/** Alt som ikke kan stå i en nettadresse et menneske skrev: mellomrom og kontrolltegn. */
// eslint-disable-next-line no-control-regex -- kontrolltegnene er nettopp det som skal fanges
const FORBIDDEN = /[\s -]/;

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Adressen slik den skal LAGRES, eller `null` hvis teksten ikke er en nettadresse.
 *
 * Ren funksjon, ingen I/O, ingen `window` — den kjører uendret i nettleseren og i API-prosessen,
 * som er hele grunnen til at den bor i `@tvitvi/shared`. Klienten normaliserer for å kunne vise
 * resultatet mens noen skriver; serveren normaliserer fordi klienten ikke er det eneste som
 * skriver til API-et (prinsipp 7).
 */
/**
 * Teksten med `https://` foran når den mangler et skjema, eller `null` når den bærer et skjema
 * som ikke er en nettadresse i det hele tatt (regel 3).
 */
function withDefaultScheme(trimmed: string): string | null {
  if (SCHEME_WITH_SLASHES.test(trimmed)) return trimmed;

  const colon = COLON_PREFIX.exec(trimmed);
  if (colon === null) return `https://${trimmed}`;

  const before = colon[1] ?? '';
  const after = colon[2] ?? '';
  // Et vertsnavn med port: enten har «skjemaet» et punktum i seg (`festival.no:8080`), eller
  // resten er bare siffer (`localhost:5173`). Alt annet er et ekte skjema, og vi vil ikke ha det.
  const looksLikeHostPort = before.includes('.') || /^\d+(\/.*)?$/.test(after);
  return looksLikeHostPort ? `https://${trimmed}` : null;
}

export function normalizeUrl(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;
  if (FORBIDDEN.test(trimmed)) return null;

  const withScheme = withDefaultScheme(trimmed);
  if (withScheme === null) return null;

  let parsed: URL;
  try {
    parsed = new URL(withScheme);
  } catch {
    return null;
  }

  if (!ALLOWED_PROTOCOLS.has(parsed.protocol)) return null;

  const host = parsed.hostname.toLowerCase();
  if (host === '') return null;
  if (host !== 'localhost' && !host.includes('.')) return null;
  // `.` og `..` som vert, og en avsluttende prikk, er `new URL`-lovlige og ikke adresser.
  if (host.startsWith('.') || host.endsWith('.') || host.includes('..')) return null;
  // Minst ett tegn på hver side av det siste punktumet: `festival.` og `.no` er ikke verter.
  if (host !== 'localhost' && !/^[^.]+(\.[^.]+)+$/.test(host)) return null;

  parsed.hostname = host;

  const text = parsed.toString();
  // `new URL('https://festival.no')` gir `https://festival.no/`. Den ene skråstreken bærer
  // ingenting og ville gjort to skrivemåter til to rader.
  return parsed.pathname === '/' && parsed.search === '' && parsed.hash === ''
    ? text.replace(/\/$/, '')
    : text;
}

/** Sann for tekst `normalizeUrl` kan lese. Brukt av felt som vil vise feilen mens man skriver. */
export function isUrlLike(input: string): boolean {
  return normalizeUrl(input) !== null;
}

/**
 * Adressen slik den skal VISES: uten `https://` og uten en avsluttende skråstrek.
 *
 * Ingen bruker leser skjemaet, og i en tabellcelle er det åtte tegn som dytter domenet ut av
 * synsfeltet. `http://` blir STÅENDE, fordi det er den ene opplysningen i skjemaet som betyr
 * noe for den som ser på: denne lenken er ikke sikret.
 */
export function displayUrl(value: string): string {
  return value.replace(/^https:\/\//i, '').replace(/\/$/, '');
}

/**
 * Feltet et skjema og et endepunkt deler.
 *
 * Normaliseringen ligger i en `transform`, så DET SOM LAGRES ER ALLTID KANONISK — også når noe
 * annet enn nettskjemaet skriver. Avvisningen ligger i en `refine` FØR den, slik at feilen fester
 * seg på strengen og blir den samme `invalid_url`-koden alle URL-felt svarer med.
 *
 * @param max Lengdegrensen kolonnen har. Den måles på det brukeren skrev, ikke på resultatet.
 */
export function urlInputSchema(max = 2048): z.ZodType<string, string> {
  return (
    z
      .string()
      .trim()
      .max(max)
      .refine(isUrlLike, { error: 'invalid_url' })
      .transform((value) => normalizeUrl(value) as string)
      // Grensen måles en gang til PÅ RESULTATET. `https://` er åtte tegn vi legger til, og en
      // kolonne på 500 tegn bryr seg om hva som havner i den, ikke om hva noen skrev.
      .refine((value) => value.length <= max, { error: 'invalid_url' })
      /*
       * OG DEN MÅ SI HVA DEN ER I OPENAPI-DOKUMENTET.
       *
       * `z.toJSONSchema(..., { io: 'output' })` kan ikke lese en `transform`: den ser en pipe uten
       * en beskrivbar utside og svarer `{}`. Målt rett etter at dette ble skrevet — `websiteUrl`
       * i `CreateFestivalRequest` gikk fra `{"type":"string","format":"uri","maxLength":2048}` til
       * det tomme skjemaet, altså «hva som helst» for en mobilklient som leser kontrakten.
       *
       * CLAUDE.md: «API-first. A mobile app is coming. The API is the contract.» En kontrakt som
       * ble vagere fordi serveren ble smartere er feil vei. `meta()` er det Zod merger inn i
       * skjemaet, og verdiene her er de samme som `z.url().max(max)` ga før.
       */
      .meta({ type: 'string', format: 'uri', maxLength: max })
  );
}
